import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Pill, Clock, RefreshCw, AlertTriangle, CheckCircle, Phone,
  Calendar, Bell, BellOff, ChevronRight, Loader2, Send,
  Package, Store, FileText, Plus, Settings, Check, X,
  History, TrendingUp, Activity, CircleDot
} from "lucide-react";

export default function MedicationManager() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [medications, setMedications] = useState([]);
  const [refillRequests, setRefillRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("medications");
  
  // Dose history
  const [doseHistory, setDoseHistory] = useState({ doses: [], overall_stats: {}, medication_stats: {} });
  const [loadingDoses, setLoadingDoses] = useState(false);
  
  // Refill dialog
  const [showRefillDialog, setShowRefillDialog] = useState(false);
  const [selectedMed, setSelectedMed] = useState(null);
  const [refillNotes, setRefillNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Dose logging dialog
  const [showDoseDialog, setShowDoseDialog] = useState(false);
  const [doseMed, setDoseMed] = useState(null);
  const [doseStatus, setDoseStatus] = useState("taken");
  const [doseNotes, setDoseNotes] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [isLoggingDose, setIsLoggingDose] = useState(false);
  
  // Settings dialog
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [settingsMed, setSettingsMed] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [medsRes, requestsRes] = await Promise.all([
        api.get("/reminders/medications"),
        api.get("/reminders/refill-requests")
      ]);
      setMedications(medsRes.data.medications || []);
      setRefillRequests(requestsRes.data.requests || []);
    } catch (error) {
      console.error("Error loading medications:", error);
      toast.error("Failed to load medications");
    } finally {
      setLoading(false);
    }
  };

  const loadDoseHistory = async () => {
    try {
      setLoadingDoses(true);
      const res = await api.get("/reminders/dose-history?days=7");
      setDoseHistory(res.data);
    } catch (error) {
      console.error("Error loading dose history:", error);
    } finally {
      setLoadingDoses(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "history" && doseHistory.doses.length === 0) {
      loadDoseHistory();
    }
  };

  const handleRequestRefill = async () => {
    if (!selectedMed) return;
    
    setIsSubmitting(true);
    try {
      const res = await api.post(`/reminders/refill-request?medication_id=${selectedMed.id}&notes=${encodeURIComponent(refillNotes)}`);
      if (res.data.success) {
        toast.success("Refill request submitted! Your pharmacy will be notified.");
        setShowRefillDialog(false);
        setSelectedMed(null);
        setRefillNotes("");
        loadData();
      } else {
        toast.info(res.data.message || "Request already pending");
      }
    } catch (error) {
      toast.error("Failed to submit refill request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecordRefill = async (medicationId) => {
    try {
      await api.post(`/reminders/medications/${medicationId}/refill`);
      toast.success("Refill recorded!");
      loadData();
    } catch (error) {
      toast.error("Failed to record refill");
    }
  };

  const handleLogDose = async () => {
    if (!doseMed) return;
    
    setIsLoggingDose(true);
    try {
      const res = await api.post(`/reminders/medications/${doseMed.id}/dose`, {
        scheduled_time: selectedTime || null,
        status: doseStatus,
        notes: doseNotes || null
      });
      
      if (res.data.success) {
        const statusMessages = {
          taken: "Dose logged! Great job staying on track.",
          skipped: "Skipped dose recorded. Your care team has been notified.",
          late: "Late dose logged. Keep up the effort!"
        };
        toast.success(statusMessages[doseStatus]);
        setShowDoseDialog(false);
        setDoseMed(null);
        setDoseStatus("taken");
        setDoseNotes("");
        setSelectedTime("");
        loadData();
        if (activeTab === "history") {
          loadDoseHistory();
        }
      }
    } catch (error) {
      toast.error("Failed to log dose");
    } finally {
      setIsLoggingDose(false);
    }
  };

  const handleToggleReminder = async (med) => {
    try {
      toast.success(med.reminder_enabled ? "Reminders disabled" : "Reminders enabled");
      loadData();
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const openDoseDialog = (med) => {
    setDoseMed(med);
    setDoseStatus("taken");
    setDoseNotes("");
    setSelectedTime("");
    setShowDoseDialog(true);
  };

  const getRefillStatus = (med) => {
    if (!med.days_until_refill && med.days_until_refill !== 0) return null;
    
    if (med.days_until_refill <= 0) {
      return { status: "overdue", color: "bg-red-500", text: "Refill needed now" };
    } else if (med.days_until_refill <= 3) {
      return { status: "urgent", color: "bg-orange-500", text: `${med.days_until_refill} days left` };
    } else if (med.days_until_refill <= 7) {
      return { status: "soon", color: "bg-yellow-500", text: `${med.days_until_refill} days left` };
    }
    return { status: "ok", color: "bg-green-500", text: `${med.days_until_refill} days left` };
  };

  const getPendingRequest = (medId) => {
    return refillRequests.find(r => r.medication_id === medId && ["pending", "submitted", "approved"].includes(r.status));
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-700",
      submitted: "bg-blue-100 text-blue-700",
      approved: "bg-green-100 text-green-700",
      denied: "bg-red-100 text-red-700",
      filled: "bg-teal-100 text-teal-700",
      picked_up: "bg-slate-100 text-slate-700"
    };
    return styles[status] || "bg-slate-100 text-slate-700";
  };

  const getDoseStatusBadge = (status) => {
    const styles = {
      taken: "bg-green-100 text-green-700",
      skipped: "bg-red-100 text-red-700",
      late: "bg-yellow-100 text-yellow-700"
    };
    return styles[status] || "bg-slate-100 text-slate-700";
  };

  const getAdherenceColor = (rate) => {
    if (rate >= 90) return "text-green-600";
    if (rate >= 70) return "text-yellow-600";
    return "text-red-600";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="medication-manager-loading">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="medication-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Pill className="w-6 h-6 text-teal-600" />
            My Medications
          </h2>
          <p className="text-slate-500 text-sm">Track doses, manage refills, and stay on schedule</p>
        </div>
        <Button variant="outline" onClick={loadData} size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="medications" data-testid="tab-medications">
            <Pill className="w-4 h-4 mr-2" />
            Medications
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="w-4 h-4 mr-2" />
            Dose History
          </TabsTrigger>
          <TabsTrigger value="refills" data-testid="tab-refills">
            <Package className="w-4 h-4 mr-2" />
            Refills
          </TabsTrigger>
        </TabsList>

        {/* Medications Tab */}
        <TabsContent value="medications" className="space-y-4">
          {/* Urgent Refills Alert */}
          {medications.filter(m => m.refill_urgent).length > 0 && (
            <Card className="bg-orange-50 border-orange-200" data-testid="urgent-refills">
              <CardHeader className="pb-2">
                <CardTitle className="text-orange-800 flex items-center gap-2 text-base">
                  <AlertTriangle className="w-5 h-5" />
                  Refills Needed Soon
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {medications.filter(m => m.refill_urgent).map(med => (
                    <div key={med.id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">{med.name}</p>
                        <p className="text-sm text-orange-600">
                          {med.days_until_refill <= 0 ? "Refill overdue" : `${med.days_until_refill} days remaining`}
                        </p>
                      </div>
                      {!getPendingRequest(med.id) ? (
                        <Button 
                          size="sm" 
                          className="bg-orange-600 hover:bg-orange-700"
                          onClick={() => {
                            setSelectedMed(med);
                            setShowRefillDialog(true);
                          }}
                        >
                          Request Refill
                        </Button>
                      ) : (
                        <Badge className={getStatusBadge(getPendingRequest(med.id).status)}>
                          {getPendingRequest(med.id).status}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Medications List */}
          <Card data-testid="medications-list">
            <CardHeader>
              <CardTitle className="text-lg">All Medications</CardTitle>
              <CardDescription>Click &quot;Log Dose&quot; when you take your medication</CardDescription>
            </CardHeader>
            <CardContent>
              {medications.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Pill className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No medications on file</p>
                  <p className="text-sm">Contact your care team to add medications</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {medications.map(med => {
                    const refillStatus = getRefillStatus(med);
                    const pendingRequest = getPendingRequest(med.id);
                    
                    return (
                      <div 
                        key={med.id} 
                        className="p-4 border border-slate-200 rounded-lg hover:border-teal-300 transition-colors"
                        data-testid={`medication-${med.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-800">{med.name}</h4>
                              {med.reminder_enabled && (
                                <Bell className="w-4 h-4 text-teal-500" />
                              )}
                            </div>
                            <p className="text-slate-600 text-sm">{med.dosage}</p>
                            <p className="text-slate-500 text-sm">{med.frequency}</p>
                            {med.instructions && (
                              <p className="text-slate-400 text-xs mt-1">{med.instructions}</p>
                            )}
                            
                            {/* Reminder times */}
                            {med.reminder_times?.length > 0 && (
                              <div className="flex items-center gap-2 mt-2">
                                <Clock className="w-3 h-3 text-slate-400" />
                                <span className="text-xs text-slate-500">
                                  Reminders: {med.reminder_times.join(", ")}
                                </span>
                              </div>
                            )}
                            
                            {/* Last dose taken */}
                            {med.last_dose_taken && (
                              <div className="flex items-center gap-2 mt-1">
                                <CheckCircle className="w-3 h-3 text-green-500" />
                                <span className="text-xs text-green-600">
                                  Last dose: {new Date(med.last_dose_taken).toLocaleString()}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="text-right space-y-2">
                            {/* Refill status */}
                            {refillStatus && (
                              <div>
                                <Badge className={`${refillStatus.color} text-white`}>
                                  {refillStatus.text}
                                </Badge>
                                {med.days_supply && (
                                  <div className="mt-1">
                                    <Progress 
                                      value={Math.max(0, Math.min(100, (med.days_until_refill / med.days_supply) * 100))} 
                                      className="w-24 h-2"
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {pendingRequest && (
                              <Badge className={getStatusBadge(pendingRequest.status)}>
                                Refill {pendingRequest.status}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                          <Button 
                            size="sm" 
                            className="bg-teal-600 hover:bg-teal-700"
                            onClick={() => openDoseDialog(med)}
                            data-testid={`log-dose-${med.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Log Dose
                          </Button>
                          {!pendingRequest && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedMed(med);
                                setShowRefillDialog(true);
                              }}
                            >
                              <Package className="w-4 h-4 mr-1" />
                              Request Refill
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleRecordRefill(med.id)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            I Picked Up
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => {
                              setSettingsMed(med);
                              setShowSettingsDialog(true);
                            }}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dose History Tab */}
        <TabsContent value="history" className="space-y-4">
          {loadingDoses ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : (
            <>
              {/* Adherence Stats */}
              <Card className="bg-gradient-to-r from-teal-500 to-teal-600 text-white" data-testid="adherence-stats">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-teal-100 text-sm">7-Day Adherence Rate</p>
                      <p className="text-4xl font-bold">
                        {doseHistory.overall_stats?.overall_adherence_rate ?? 0}%
                      </p>
                      <p className="text-teal-100 text-sm mt-1">
                        {doseHistory.overall_stats?.total_taken ?? 0} of {doseHistory.overall_stats?.total_logged ?? 0} doses taken
                      </p>
                    </div>
                    <div className="text-right">
                      <TrendingUp className="w-12 h-12 text-teal-200" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Per-Medication Stats */}
              <Card data-testid="medication-adherence">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-teal-600" />
                    Medication Adherence
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(doseHistory.medication_stats || {}).length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No dose history yet</p>
                      <p className="text-sm">Start logging doses to track your adherence</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(doseHistory.medication_stats || {}).map(([medId, stats]) => (
                        <div key={medId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-slate-800">{stats.medication_name}</p>
                            <p className="text-sm text-slate-500">{stats.dosage}</p>
                          </div>
                          <div className="text-right">
                            <p className={`text-2xl font-bold ${getAdherenceColor(stats.adherence_rate)}`}>
                              {stats.adherence_rate}%
                            </p>
                            <p className="text-xs text-slate-500">
                              {stats.taken}/{stats.total_logged} taken
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Dose Logs */}
              <Card data-testid="recent-doses">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="w-5 h-5 text-slate-600" />
                    Recent Dose Logs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-64">
                    {(doseHistory.doses || []).length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No doses logged yet</p>
                    ) : (
                      <div className="space-y-2">
                        {(doseHistory.doses || []).map(dose => (
                          <div key={dose.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-8 rounded-full ${
                                dose.status === 'taken' ? 'bg-green-500' : 
                                dose.status === 'skipped' ? 'bg-red-500' : 'bg-yellow-500'
                              }`} />
                              <div>
                                <p className="font-medium text-slate-800">{dose.medication_name}</p>
                                <p className="text-xs text-slate-500">
                                  {new Date(dose.taken_at).toLocaleString()}
                                </p>
                                {dose.notes && (
                                  <p className="text-xs text-slate-400 mt-1">{dose.notes}</p>
                                )}
                              </div>
                            </div>
                            <Badge className={getDoseStatusBadge(dose.status)}>
                              {dose.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Refills Tab */}
        <TabsContent value="refills" className="space-y-4">
          <Card data-testid="refill-history">
            <CardHeader>
              <CardTitle className="text-lg">Refill Requests</CardTitle>
              <CardDescription>Track and manage your medication refill requests</CardDescription>
            </CardHeader>
            <CardContent>
              {refillRequests.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No refill requests</p>
                  <p className="text-sm">Request a refill from the Medications tab</p>
                </div>
              ) : (
                <ScrollArea className="h-64">
                  <div className="space-y-2">
                    {refillRequests.map(req => (
                      <div key={req.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-800">{req.medication_name}</p>
                          <p className="text-xs text-slate-500">
                            Requested {new Date(req.requested_at).toLocaleDateString()}
                          </p>
                          {req.estimated_ready_date && (
                            <p className="text-xs text-teal-600">
                              Ready: {new Date(req.estimated_ready_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <Badge className={getStatusBadge(req.status)}>
                          {req.status.replace("_", " ")}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Log Dose Dialog */}
      <Dialog open={showDoseDialog} onOpenChange={setShowDoseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-teal-600" />
              Log Dose
            </DialogTitle>
            <DialogDescription>
              Record when you take your medication
            </DialogDescription>
          </DialogHeader>
          
          {doseMed && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <h4 className="font-semibold">{doseMed.name}</h4>
                <p className="text-sm text-slate-600">{doseMed.dosage}</p>
              </div>
              
              {/* Dose Status */}
              <div>
                <Label>Did you take this dose?</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <Button
                    variant={doseStatus === "taken" ? "default" : "outline"}
                    className={doseStatus === "taken" ? "bg-green-600 hover:bg-green-700" : ""}
                    onClick={() => setDoseStatus("taken")}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Taken
                  </Button>
                  <Button
                    variant={doseStatus === "late" ? "default" : "outline"}
                    className={doseStatus === "late" ? "bg-yellow-600 hover:bg-yellow-700" : ""}
                    onClick={() => setDoseStatus("late")}
                  >
                    <Clock className="w-4 h-4 mr-1" />
                    Late
                  </Button>
                  <Button
                    variant={doseStatus === "skipped" ? "default" : "outline"}
                    className={doseStatus === "skipped" ? "bg-red-600 hover:bg-red-700" : ""}
                    onClick={() => setDoseStatus("skipped")}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Skipped
                  </Button>
                </div>
              </div>
              
              {/* Scheduled Time (if reminders set) */}
              {doseMed.reminder_times?.length > 0 && (
                <div>
                  <Label>Which scheduled dose?</Label>
                  <Select value={selectedTime} onValueChange={setSelectedTime}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select time (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {doseMed.reminder_times.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Notes */}
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={doseNotes}
                  onChange={(e) => setDoseNotes(e.target.value)}
                  placeholder={doseStatus === "skipped" ? "Why did you skip this dose?" : "Any notes about this dose..."}
                  className="mt-2"
                />
              </div>
              
              {doseStatus === "skipped" && (
                <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700">
                  <p>Your care team will be notified about this skipped dose so they can provide support.</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDoseDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleLogDose}
              disabled={isLoggingDose}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {isLoggingDose ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Log Dose
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refill Request Dialog */}
      <Dialog open={showRefillDialog} onOpenChange={setShowRefillDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-teal-600" />
              Request Refill
            </DialogTitle>
            <DialogDescription>
              Submit a refill request for {selectedMed?.name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedMed && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <h4 className="font-semibold">{selectedMed.name}</h4>
                <p className="text-sm text-slate-600">{selectedMed.dosage}</p>
                {selectedMed.pharmacy_name && (
                  <p className="text-sm text-slate-500 mt-1">
                    Pharmacy: {selectedMed.pharmacy_name}
                  </p>
                )}
                {selectedMed.prescription_number && (
                  <p className="text-xs text-slate-400">
                    Rx#: {selectedMed.prescription_number}
                  </p>
                )}
              </div>
              
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={refillNotes}
                  onChange={(e) => setRefillNotes(e.target.value)}
                  placeholder="Any special instructions or notes for the pharmacy..."
                  className="mt-2"
                />
              </div>
              
              <div className="p-3 bg-teal-50 rounded-lg text-sm text-teal-700">
                <p>Your pharmacy will be notified automatically. You&apos;ll receive updates when your medication is ready.</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefillDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestRefill}
              disabled={isSubmitting}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Medication Settings</DialogTitle>
            <DialogDescription>
              Configure reminders for {settingsMed?.name}
            </DialogDescription>
          </DialogHeader>
          
          {settingsMed && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Medication Reminders</Label>
                  <p className="text-sm text-slate-500">Get notified when it&apos;s time to take this medication</p>
                </div>
                <Switch 
                  checked={settingsMed.reminder_enabled}
                  onCheckedChange={() => handleToggleReminder(settingsMed)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Refill</Label>
                  <p className="text-sm text-slate-500">Automatically request refills when running low</p>
                </div>
                <Switch checked={settingsMed.auto_refill} />
              </div>
              
              {settingsMed.reminder_times?.length > 0 && (
                <div>
                  <Label>Reminder Times</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {settingsMed.reminder_times.map((time, i) => (
                      <Badge key={i} variant="outline">{time}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {settingsMed.pharmacy_name && (
                <div>
                  <Label>Pharmacy</Label>
                  <p className="text-sm text-slate-600 mt-1">{settingsMed.pharmacy_name}</p>
                  {settingsMed.pharmacy_phone && (
                    <a href={`tel:${settingsMed.pharmacy_phone}`} className="text-sm text-teal-600 hover:underline">
                      {settingsMed.pharmacy_phone}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowSettingsDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
